import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Handshake, Copy, Users, Euro, TrendingUp, Wallet, ArrowLeft, MapPin } from "lucide-react";

const STATUS_LABEL = {
  escrow_paid: "Séquestré",
  shipped: "Expédié",
  released: "Libéré",
  disputed: "Litige",
  refunded: "Remboursé",
};

export default function AgentDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("filleuls");

  useEffect(() => {
    if (!loading && (!user || !["agent", "admin"].includes(user.role))) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || !["agent", "admin"].includes(user.role)) return;
    api.get("/agent/dashboard").then(r => setData(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [user]);

  if (!user || !["agent", "admin"].includes(user.role)) return <div className="p-20 text-center">Accès agent requis</div>;
  if (!data) return <div className="p-20 text-center">Chargement…</div>;

  const copyCode = () => {
    navigator.clipboard.writeText(data.referral_code || "");
    toast.success("Code copié ✓");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="agent-dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-[color:var(--color-mint)] border-2 border-black flex items-center justify-center">
            <Handshake size={26} strokeWidth={2.5} />
          </div>
          <div>
            <p className="label flex items-center gap-2">Agent FRIPRI {data.department && <span className="pill pill-mint inline-flex items-center gap-1"><MapPin size={10}/> Dépt {data.department}</span>}</p>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tighter">Mon secteur</h1>
          </div>
        </div>
        <Link to="/dashboard" className="btn btn-ghost btn-sm"><ArrowLeft size={14}/> Mon espace</Link>
      </div>

      {/* Code de parrainage */}
      <div className="card border-2 border-black bg-black text-white p-4 lg:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="referral-code-card">
        <div className="flex-1">
          <p className="label text-[color:var(--color-mint)]">Mon code de parrainage</p>
          <p className="font-black text-3xl tracking-tight display-num" data-testid="referral-code-value">{data.referral_code || "—"}</p>
          <p className="text-xs text-white/70 mt-1">Partage ce code aux vendeurs de ton secteur : tu gagnes {(data.commission_pct * 100).toFixed(0)}% sur chacune de leurs ventes.</p>
        </div>
        <button onClick={copyCode} className="btn btn-mint btn-sm shrink-0" data-testid="copy-referral-code-btn">
          <Copy size={14} strokeWidth={3}/> Copier
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <Stat icon={<Users size={18} strokeWidth={2.5}/>} label="Filleuls" value={data.stats.referred_count} color="bg-white" testid="stat-filleuls"/>
        <Stat icon={<TrendingUp size={18} strokeWidth={2.5}/>} label="Ventes filleuls" value={`${data.stats.referred_sales_total.toFixed(0)}€`} color="bg-[color:var(--color-mint)]" testid="stat-ventes-filleuls"/>
        <Stat icon={<Euro size={18} strokeWidth={2.5}/>} label="Mes ventes" value={`${data.stats.my_sales_total.toFixed(0)}€`} color="bg-white" testid="stat-mes-ventes"/>
        <Stat icon={<Wallet size={18} strokeWidth={2.5}/>} label="Comm. en attente" value={`${data.stats.commission_pending.toFixed(2)}€`} color="bg-[color:var(--color-yellow)]" testid="stat-comm-pending"/>
        <Stat icon={<Wallet size={18} strokeWidth={2.5}/>} label="Comm. versées" value={`${data.stats.commission_paid.toFixed(2)}€`} color="bg-black text-white" testid="stat-comm-paid"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar -mx-4 px-4">
        {[
          { k: "filleuls", label: "Mes filleuls" },
          { k: "commissions", label: "Mes commissions" },
          { k: "ventes-filleuls", label: "Ventes filleuls" },
          { k: "mes-ventes", label: "Mes ventes" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`btn btn-sm shrink-0 ${tab === t.k ? "btn-primary" : "btn-ghost"}`} data-testid={`agent-tab-${t.k}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "filleuls" && (
        <div className="space-y-2" data-testid="filleuls-list">
          {data.referred_sellers.length === 0 && (
            <div className="text-center py-16 border-2 border-dashed border-black">
              <p className="text-2xl font-black mb-2">Aucun filleul pour l'instant 🤝</p>
              <p className="text-sm text-[color:var(--color-text-muted)]">Partage ton code <strong>{data.referral_code}</strong> aux vendeurs de ton département.</p>
            </div>
          )}
          {data.referred_sellers.map(s => (
            <div key={s.user_id} className="bg-white p-3 flex items-center gap-3 border-2 border-black" data-testid={`filleul-${s.user_id}`}>
              <div className="w-10 h-10 bg-black text-white flex items-center justify-center font-bold">{s.name?.[0]?.toUpperCase() || "?"}</div>
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{s.name}</p>
                <p className="text-xs text-[color:var(--color-text-muted)] truncate">{s.email}</p>
              </div>
              <p className="text-xs text-[color:var(--color-text-muted)] shrink-0">Depuis le {new Date(s.referred_at || s.created_at).toLocaleDateString("fr-FR")}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "commissions" && (
        <div className="space-y-2" data-testid="commissions-list">
          {data.commissions.length === 0 && <p className="text-center py-12 text-[color:var(--color-text-muted)]">Aucune commission pour l'instant. Elles apparaissent quand une vente d'un filleul est finalisée.</p>}
          {data.commissions.map(c => (
            <div key={c.commission_id} className="bg-white p-3 flex items-center justify-between gap-3 border-2 border-black" data-testid={`commission-${c.commission_id}`}>
              <div className="min-w-0">
                <p className="label">#{c.order_id?.slice(-8).toUpperCase()} · {new Date(c.created_at).toLocaleDateString("fr-FR")}</p>
                <p className="font-bold truncate">Vente de {c.seller_name} · {c.sale_amount?.toFixed(2)}€</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-black text-xl" style={{ color: "var(--color-coral)" }}>+{c.commission_amount?.toFixed(2)}€</p>
                <span className={`pill ${c.status === "transferred" ? "pill-mint" : ""}`}>{c.status === "transferred" ? "Versée" : "En attente"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "ventes-filleuls" && <OrderList orders={data.referred_orders} showSellers testid="referred-orders-list" empty="Aucune vente de tes filleuls pour l'instant."/>}
      {tab === "mes-ventes" && <OrderList orders={data.my_orders} testid="my-orders-list" empty="Aucune vente personnelle pour l'instant."/>}
    </div>
  );
}

function Stat({ icon, label, value, color, testid }) {
  return (
    <div className={`p-4 border-2 border-black ${color}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">{icon}<span className="label">{label}</span></div>
      <div className="display-num text-2xl lg:text-3xl font-black">{value}</div>
    </div>
  );
}

function OrderList({ orders, showSellers, testid, empty }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      {orders.length === 0 && <p className="text-center py-12 text-[color:var(--color-text-muted)]">{empty}</p>}
      {orders.map(o => (
        <div key={o.order_id} className="bg-white p-3 flex items-center justify-between gap-3 border-2 border-black">
          <div className="min-w-0">
            <p className="label">#{o.order_id.slice(-8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
            <p className="font-bold">{o.product_ids?.length || 0} article(s){showSellers && o.seller_names?.length ? ` · ${o.seller_names.join(", ")}` : ""}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-black text-xl" style={{ color: "var(--color-coral)" }}>{o.amount?.toFixed(0)}€</p>
            <span className={`pill ${o.status === "released" ? "pill-mint" : o.status === "disputed" ? "pill-coral" : ""}`}>{STATUS_LABEL[o.status] || o.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
